<script setup lang="ts">
import frameSrc from '~/assets/images/clients/rect-glass.webp'
import { useIntersectionObserver, useIntervalFn } from '@vueuse/core'
import { gsap } from 'gsap'

const props = defineProps<{
  data: SectionClientListRecord
  moduleIndex: number
}>()

const { invertedStyles } = useSectionRules(props.moduleIndex, props.data.invertColorTheme)

const frontLogos = ref(
  props.data?.records.slice(0, Math.ceil((props.data?.records.length ?? 0) / 2)) ?? []
)
const backLogos = ref(
  props.data?.records.slice(Math.ceil((props.data?.records.length ?? 0) / 2)) ?? []
)

let ctx: gsap.Context | null = null
const duration = 30
const interval = 6

// Calculate positions dynamically
const calculatePosition = (index: number, total: number, radius: number) => {
  const angle = (2 * Math.PI * index) / total
  return {
    x: 500 + Math.cos(angle) * radius - 60,
    y: 500 + Math.sin(angle) * radius - 60
  }
}

const animationReverse = ref(false)
const rotationAnimation = ref<GSAPTween | null>(null)

const switchPositions = () => {
  const totalFront = frontLogos.value.length
  const totalBack = backLogos.value.length

  frontLogos.value.forEach((_, index) => {
    const { x, y } = calculatePosition(index, totalBack, animationReverse.value ? 260 : 400)
    const wrapper = document.querySelector(`.fx-position-front-${index}`) as SVGGElement
    if (wrapper) {
      gsap.to(wrapper, {
        duration: 1.5,
        ease: 'power2.inOut',
        attr: { transform: `translate(${x}, ${y})` },
        opacity: animationReverse.value ? 0.2 : 1
        //filter: animationReverse.value ? 'blur(12px)' : 'blur(0px)'
      })
    }
  })

  backLogos.value.forEach((_, index) => {
    const { x, y } = calculatePosition(index, totalFront, animationReverse.value ? 400 : 260)
    const wrapper = document.querySelector(`.fx-position-back-${index}`) as SVGGElement
    if (wrapper) {
      gsap.to(wrapper, {
        duration: 1.5,
        ease: 'power2.inOut',
        attr: { transform: `translate(${x}, ${y})` },
        opacity: animationReverse.value ? 1 : 0.2
        //filter: animationReverse.value ? 'blur(0px)' : 'blur(12px)'
      })
    }
  })

  animationReverse.value = !animationReverse.value
}

const sectionRef = ref<HTMLElement | null>(null)
const isVisible = ref(false)

const { stop } = useIntersectionObserver(
  sectionRef,
  ([{ isIntersecting }], _observerElement) => {
    if (isIntersecting) {
      isVisible.value = true
      rotationAnimation.value?.play()
    }
  },
  {
    threshold: 0.5
  }
)

const rectRef = ref<HTMLElement | null>(null)

function onMouseMove(e: MouseEvent) {
  const canvasContainer = sectionRef.value

  if (!canvasContainer) return

  const rect = canvasContainer.getBoundingClientRect()
  const isMouseInside =
    e.clientX >= rect.left &&
    e.clientX <= rect.right &&
    e.clientY >= rect.top &&
    e.clientY <= rect.bottom

  const coords = getMousePos(e)

  const maxMovementX = 50

  if (!isMouseInside) {
    return
  }

  // Calculate the new camera position based on the mouse position
  let movementX = 0

  if (coords.x > window.innerWidth / 2) {
    movementX = (coords.x - window.innerWidth / 2) / 25
  } else if (coords.x < window.innerWidth / 2) {
    movementX = -(window.innerWidth / 2 - coords.x) / 25
  }

  // Clamp the movement within the max bounds
  const clampedX = Math.max(-maxMovementX, Math.min(maxMovementX, movementX))

  // Smoothly move the camera to the new position
  gsap.to(rectRef.value, {
    x: clampedX,
    ease: 'power3.out',
    duration: 0.3 // Smooth transition duration
  })
}

onMounted(() => {
  nextTick(() => {
    document.addEventListener('mousemove', onMouseMove)

    ctx = gsap.context(() => {
      // Set initial positions and blur states
      frontLogos.value.forEach((_, index) => {
        const { x, y } = calculatePosition(index, frontLogos.value.length, 400)
        gsap.set(`.fx-position-front-${index}`, {
          attr: { transform: `translate(${x}, ${y})` },
          opacity: 1
          //filter: 'blur(0px)'
        })
      })

      backLogos.value.forEach((_, index) => {
        const { x, y } = calculatePosition(index, backLogos.value.length, 260)
        gsap.set(`.fx-position-back-${index}`, {
          attr: { transform: `translate(${x}, ${y})` },
          opacity: 0.2
          //filter: 'blur(12px)'
        })
      })

      // Main rotation animation
      rotationAnimation.value = gsap.to('.fx-logo', {
        duration,
        rotation: 360,
        svgOrigin: '500 500',
        transformOrigin: 'center center',
        onUpdate: function () {
          const currentRotation = this.progress() * 360
          document.querySelectorAll('.fx-image').forEach((image) => {
            gsap.set(image, {
              rotation: -currentRotation,
              transformOrigin: 'center center',
              ease: 'none'
            })
          })
        },
        ease: 'none',
        repeat: -1
      })
    })

    useIntervalFn(() => {
      switchPositions()
    }, interval * 1000)
  })
})

onUnmounted(() => {
  stop()

  if (ctx) {
    ctx.revert()
  }

  document.removeEventListener('mousemove', onMouseMove)
})
</script>

<template>
  <div
    ref="sectionRef"
    class="z-[0] overflow-hidden py-12 shadow-[0_-20px_30px_20px_rgba(255,255,255,0.99)] lg:py-20"
    :class="invertedStyles"
  >
    <DeliciousSausage class="absolute left-0 top-0 -translate-x-1/4" name="clients-top" />
    <DeliciousSausage
      class="absolute bottom-5 right-0 hidden translate-x-1/4 lg:block"
      name="clients-bot"
    />
    <div v-if="data" class="relative mx-auto aspect-square w-full max-w-[660px]">
      <div
        ref="rectRef"
        class="absolute left-1/2 top-1/2 isolate z-[3] -translate-x-1/2 -translate-y-1/2 transition-all"
      >
        <div
          class="absolute left-3 top-3 z-[2] h-[calc(100%-24px)] w-[calc(100%-24px)] rounded-full bg-white/80"
        ></div>
        <NuxtLink to="/clients">
          <Image
            loading="lazy"
            class="relative z-[4] mb-5 h-30 w-30 lg:mb-8 lg:h-[178px] lg:w-[178px]"
            :src="frameSrc"
            :width="178"
            :height="178"
            alt="Glass Frame"
          />
        </NuxtLink>
      </div>
      <div
        class="absolute inset-0 transition-transform duration-1000"
        :class="isVisible ? 'scale-1' : 'scale-0'"
      >
        <svg id="demo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">
          <title v-if="data.title">{{ data.title }}</title>

          <!-- Front Logos -->
          <g
            v-for="(record, index) in frontLogos"
            :key="'front-' + index"
            class="fx-logo fx-logo-front will-change-transform"
            :data-index="index"
          >
            <g :class="`fx-position fx-position-front-${index}`">
              <image
                v-if="record.logo"
                class="fx-image"
                :width="120"
                :height="120"
                :href="
                  record.logo.responsiveImage ? record.logo.responsiveImage.src : record.logo.url
                "
                :alt="record.logo.alt ?? 'Client logo'"
              />
            </g>
          </g>

          <!-- Back Logos -->
          <g
            v-for="(record, index) in backLogos"
            :key="'back-' + index"
            class="fx-logo fx-logo-back will-change-transform"
            :data-index="index"
          >
            <g :class="`fx-position fx-position-back-${index}`">
              <image
                v-if="record.logo"
                class="fx-image"
                :width="120"
                :height="120"
                :href="
                  record.logo.responsiveImage ? record.logo.responsiveImage.src : record.logo.url
                "
                :alt="record.logo.alt ?? 'Client logo'"
              />
            </g>
          </g>

          <text
            v-if="data.title"
            class="font-sans"
            text-anchor="middle"
            transform="translate(500 500)"
            fill="#00000"
            font-size="64"
            font-weight="900"
          >
            {{ data.title }}
          </text>
        </svg>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.fx-image {
  @apply opacity-50;
}
</style>
